import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import {
  GetOrderById,
  IOrder,
  MarkOrderAsRefunded,
} from "../../services/UserSubscriptionServices";
import { formatCurrency, getReadableDateString } from "../../utils/commons";
import InvoiceDialog from "./InvoiceDialog";

interface OrderDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  orderId: string | null;
  onRefundSuccess?: () => void;
}

const getPaymentColor = (status: string) => {
  if (status === "PAID" || status === "PAID_MANUAL") return "success";
  if (status === "REFUNDED") return "warning";
  if (status === "FAILED") return "error";
  return "info";
};

const getPaymentModeLabel = (mode?: string) => {
  if (mode === "APP") return "App";
  if (mode === "CASH") return "Cash";
  if (mode === "ONLINE") return "Online";
  return "N/A";
};

const getProgramDiscountAmount = (price: number, discountPercent: number) =>
  (price * discountPercent) / 100;

export const OrderDetailDrawer = ({
  open,
  onClose,
  orderId,
  onRefundSuccess,
}: OrderDetailDrawerProps): JSX.Element => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<IOrder | null>(null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReferenceId, setRefundReferenceId] = useState("");
  const [refundNotes, setRefundNotes] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);

  useEffect(() => {
    if (open && orderId) {
      setLoading(true);
      GetOrderById({ orderId })
        .then(setOrder)
        .catch(() =>
          enqueueSnackbar("Failed to load order", { variant: "error" })
        )
        .finally(() => setLoading(false));
    }
  }, [open, orderId, enqueueSnackbar]);

  const handleClose = () => {
    setOrder(null);
    onClose();
  };

  const canRefund =
    order?.userSubscription?.status === "CANCELLED" &&
    (order?.paymentStatus === "PAID" || order?.paymentStatus === "PAID_MANUAL");

  const canViewInvoice =
    order?.paymentStatus === "PAID" ||
    order?.paymentStatus === "PAID_MANUAL" ||
    order?.paymentStatus === "REFUNDED";

  const openRefundDialog = () => {
    setRefundAmount(order?.amount?.toString() || "");
    setRefundReferenceId("");
    setRefundNotes("");
    setRefundDialogOpen(true);
  };

  const handleRefundSubmit = async () => {
    if (!order || !refundAmount || !refundNotes.trim()) return;

    setRefundLoading(true);
    try {
      const updatedOrder = await MarkOrderAsRefunded(order.orderId, {
        refundedAmount: parseFloat(refundAmount),
        refundReferenceId: refundReferenceId.trim() || undefined,
        refundNotes: refundNotes.trim(),
      });
      setOrder(updatedOrder);
      enqueueSnackbar("Order marked as refunded", { variant: "success" });
      setRefundDialogOpen(false);
      onRefundSuccess?.();
    } catch {
      enqueueSnackbar("Failed to mark order as refunded", { variant: "error" });
    } finally {
      setRefundLoading(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: { xs: "100%", sm: 400 }, p: 3 } }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">Order Details</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} height={40} sx={{ mb: 2 }} />
            ))}
          </Box>
        ) : order ? (
          <Box>
            <Row label="Order ID" value={order.orderId} />
            <Row label="Payment ID" value={order.paymentId || "N/A"} />
            <Divider sx={{ my: 2 }} />

            <Row label="Program" value={order.programName || "N/A"} />
            <Row label="Original Price" value={formatCurrency(order.price)} />
            {order.discount > 0 && (
              <Row
                label={`Program Discount (${order.discount}% OFF)`}
                value={`-${formatCurrency(
                  getProgramDiscountAmount(order.price, order.discount)
                )}`}
                color={theme.palette.success.main}
              />
            )}
            <Row
              label="Final Amount"
              value={formatCurrency(order.amount)}
              bold
            />
            <Divider sx={{ my: 2 }} />

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="body2" color="textSecondary">
                Payment Status
              </Typography>
              <Chip
                label={
                  order.paymentStatus === "PAID_MANUAL"
                    ? "Paid (Manual)"
                    : order.paymentStatus
                }
                color={getPaymentColor(order.paymentStatus)}
                size="small"
              />
            </Box>

            <Row
              label="Payment Mode"
              value={getPaymentModeLabel(order.paymentMode)}
            />
            <Row
              label="Paid At"
              value={
                order.paidAt
                  ? getReadableDateString(order.paidAt) || "N/A"
                  : "N/A"
              }
            />

            {/* Refund Info Section */}
            {order.paymentStatus === "REFUNDED" && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1.5, color: theme.palette.warning.main }}
                >
                  Refund Details
                </Typography>
                <Row
                  label="Refunded Amount"
                  value={formatCurrency(order.refundedAmount || 0)}
                />
                <Row
                  label="Refunded At"
                  value={
                    order.refundedAt
                      ? getReadableDateString(order.refundedAt) || "N/A"
                      : "N/A"
                  }
                />
                <Row label="Refunded By" value={order.refundedBy || "N/A"} />
                {order.refundReferenceId && (
                  <Row
                    label="Refund Reference ID"
                    value={order.refundReferenceId}
                  />
                )}
                {order.refundNotes && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 0.5 }}
                    >
                      Refund Notes
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        p: 1,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {order.refundNotes}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {canViewInvoice && (
              <Button
                variant="outlined"
                fullWidth
                sx={{ mb: canRefund ? 1.5 : 0 }}
                onClick={() => setInvoiceDialogOpen(true)}
              >
                View Invoice
              </Button>
            )}

            {canRefund && (
              <Button
                variant="outlined"
                color="warning"
                fullWidth
                onClick={openRefundDialog}
              >
                Mark as Refunded
              </Button>
            )}
          </Box>
        ) : (
          <Typography color="textSecondary" align="center">
            No order data available
          </Typography>
        )}
      </Drawer>

      {/* Refund Dialog */}
      <Dialog
        open={refundDialogOpen}
        onClose={() => !refundLoading && setRefundDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark Order as Refunded</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            This is for bookkeeping purposes only. Please process the actual
            refund through Razorpay dashboard.
          </Typography>
          <TextField
            label="Refunded Amount"
            type="number"
            fullWidth
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            sx={{ mb: 2 }}
            error={
              !!refundAmount && parseFloat(refundAmount) > (order?.amount || 0)
            }
            helperText={
              !!refundAmount && parseFloat(refundAmount) > (order?.amount || 0)
                ? `Cannot exceed order amount: ${formatCurrency(
                    order?.amount || 0
                  )}`
                : `Order amount: ${formatCurrency(order?.amount || 0)}`
            }
          />
          <TextField
            label="Razorpay Refund ID (Optional)"
            fullWidth
            value={refundReferenceId}
            onChange={(e) => setRefundReferenceId(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="e.g., rfnd_xyz123"
            helperText="Paste the Refund ID from Razorpay dashboard for traceability"
          />
          <TextField
            label="Refund Notes"
            fullWidth
            multiline
            rows={3}
            value={refundNotes}
            onChange={(e) => setRefundNotes(e.target.value)}
            placeholder="e.g., Customer requested refund due to..."
            helperText="Reason for refund or additional details"
            required
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setRefundDialogOpen(false)}
            disabled={refundLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRefundSubmit}
            disabled={
              refundLoading ||
              !refundAmount ||
              parseFloat(refundAmount) <= 0 ||
              parseFloat(refundAmount) > (order?.amount || 0) ||
              !refundNotes.trim()
            }
            startIcon={refundLoading && <CircularProgress size={16} />}
          >
            Confirm Refund
          </Button>
        </DialogActions>
      </Dialog>

      <InvoiceDialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        orderId={order?.orderId || null}
      />
    </>
  );
};

const Row = ({
  label,
  value,
  color,
  bold,
}: {
  label: string;
  value: string;
  color?: string;
  bold?: boolean;
}) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: bold ? 600 : 400, color }}>
      {value}
    </Typography>
  </Box>
);

export default OrderDetailDrawer;
